import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import RoutePaths from '../routes/RoutePaths';

export default function useArticleRoute() {
    const { articleId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const visitedFeedKeys = useRef(new Set());
    const feedPath = articleId
        ? location.pathname.replace(/\/article\/[^/]+\/?$/, '') || RoutePaths.newsQueue.root
        : location.pathname;

    useEffect(() => {
        if (!articleId) visitedFeedKeys.current.add(location.key);
    }, [articleId, location.key]);

    const openArticle = useCallback((article) => {
        if (article?.id == null) return;
        const prefix = feedPath === '/' ? '' : feedPath;
        navigate({
            pathname: `${prefix}/article/${encodeURIComponent(String(article.id))}`,
            search: location.search,
            hash: location.hash,
        }, {
            replace: Boolean(articleId),
            state: {
                ...location.state,
                articleFeedKey: articleId ? location.state?.articleFeedKey : location.key,
            },
        });
    }, [articleId, feedPath, location, navigate]);

    const closeArticle = useCallback(() => {
        const feedKey = location.state?.articleFeedKey;
        // Only go back when this mounted feed observed the originating history entry.
        // A refreshed/direct link must never send the user to an external page.
        if (feedKey && visitedFeedKeys.current.has(feedKey)) {
            navigate(-1);
            return;
        }
        const feedState = { ...location.state };
        delete feedState.articleFeedKey;
        navigate({ pathname: feedPath, search: location.search, hash: location.hash }, {
            replace: true,
            state: feedState,
        });
    }, [feedPath, location, navigate]);

    return { articleId, openArticle, closeArticle };
}
